// Generated with util/create-component.js
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notification, Form, Input, Button } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

import routes from 'routing/routes';

import { AuthenticationActionsContext } from 'containers/data_context';
import styles from './verify_reset_password_email_page.module.scss';

const VerifyResetPasswordEmailPage: React.FC = () => {
  const history = useHistory();
  const { resetEmailPassword } = useContext(AuthenticationActionsContext);

  const { t } = useTranslation();

  const [form] = Form.useForm();

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const formItemLayout = {
    labelCol: { xs: { span: 24 }, sm: { span: 8 } },
    wrapperCol: { xs: { span: 24 }, sm: { span: 16 } },
  };

  const initialValues = {
    password: '',
    passwordConfirmation: '',
  };

  const validateMessages = {
    required: 'required',
  };

  const handleSubmit = async (): Promise<void> => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) return;

    const resetPasswordData = form.getFieldsValue(true);

    try {
      setIsSaving(true);
      const params = {
        ...resetPasswordData,
        token,
      };
      await resetEmailPassword({ resetEmailPasswordParams: params });
      openNotificationWithIcon('success');
      history.push(routes.loginPage);
    } catch (e) {
      console.log(e, 'error');
      openNotificationWithIcon('error');
    } finally {
      setIsSaving(false);
    }
  };

  const openNotificationWithIcon = (type: 'success' | 'error') => {
    if (type === 'success') {
      return notification['success']({
        message: t('general.success_message'),
      });
    } else {
      return notification['error']({
        message: t('general.error_message'),
        description: t('general.error_description'),
      });
    }
  };

  return (
    <div data-testid="VerifyResetPasswordEmailPage" className={styles.root}>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleSubmit}
        {...formItemLayout}
        validateMessages={validateMessages}
        className={styles.form_wrapper}
      >
        <Form.Item
          label={t('general.other.password')}
          name="password"
          rules={[
            () => ({
              validator(_, value) {
                if (!value) {
                  return Promise.reject(new Error(t('validation_messages.required')));
                }

                if (value.length < 8) {
                  return Promise.reject(new Error(t('general.password.validation_min')));
                }

                if (value.length > 72) {
                  return Promise.reject(new Error(t('general.password.validation_max')));
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={t('general.other.password')}
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item
          label={t('general.password.confirm_password')}
          name="passwordConfirmation"
          rules={[
            {
              required: true,
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error(t('general.password.confirm_password.not_match')));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={t('general.password.confirm_password')}
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <div className={styles.footer}>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isSaving} className={styles.save_button}>
              {t('link.save_changes')}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default VerifyResetPasswordEmailPage;
